import { PageHeader, Marquee, CTABand, Counter } from "../components/ui-blocks";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { STATS } from "../data";
import { Target, Heart, Compass, Zap } from "lucide-react";

const VALUES = [
  { icon: Target, title: "Precision over volume", desc: "We'd rather send three perfect candidates than thirty adequate ones. Fit is everything." },
  { icon: Heart, title: "People first, always", desc: "Every placement changes two lives — the candidate's and the client's. We never forget that." },
  { icon: Compass, title: "Radical transparency", desc: "No hidden fees, no ghosting, no spin. You always know exactly where a search stands." },
  { icon: Zap, title: "Bias for momentum", desc: "Great talent doesn't wait. Neither do we. Speed is a feature of quality, not its enemy." },
];

const TIMELINE = [
  { year: "2013", title: "Founded in San Francisco", desc: "Three recruiters with a conviction: staffing could be a partnership, not a transaction." },
  { year: "2017", title: "Crossed 5,000 placements", desc: "Expanded into healthcare and finance as demand for specialized talent surged." },
  { year: "2021", title: "Launched RPO & EOR", desc: "Became a full workforce partner — from a single hire to fully outsourced recruiting." },
  { year: "2026", title: "500+ companies, 15K+ placements", desc: "Now serving six industries nationwide with a 98% client retention rate." },
];

const TEAM = [
  { name: "Elena Cortez", role: "Founder & CEO", img: "https://images.unsplash.com/photo-1758518727592-706e80ebc354?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { name: "Marcus Bellamy", role: "Head of Executive Search", img: "https://images.unsplash.com/photo-1758691737644-ef8be18256c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { name: "Priya Nair", role: "VP of RPO Solutions", img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader overline="About Hirezen" title="We don't fill seats. We build teams that last." subtitle="For over a decade, Hirezen has connected exceptional people with companies where they thrive — and stay." />
      <Marquee />

      {/* Story + stats */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="clip-frame overflow-hidden rounded-2xl border border-slate-800">
              <img src="https://images.unsplash.com/photo-1624555130581-1d9cca783bc0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Hirezen team" className="w-full h-[440px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <p className="overline mb-4">Our Story</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight leading-tight mb-5">A consultative approach, built to compound.</h2>
            <p className="text-slate-400 leading-relaxed mb-4">We started Hirezen because staffing had lost the plot. Résumés were being sprayed, relationships treated as disposable, and both companies and candidates left frustrated. We believed the industry could be run on trust instead.</p>
            <p className="text-slate-400 leading-relaxed">Today that conviction is measurable: 98% of our clients return year after year, and 92% of the people we place are still thriving in their roles a year later. When people stay, momentum builds — and that's the flywheel we engineer for.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 border-y border-slate-800/80 bg-[#0d1220]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {STATS.map((s) => (
              <StaggerItem key={s.label}><div className="font-accent text-4xl sm:text-5xl font-bold text-gradient-amber"><Counter value={s.value} suffix={s.suffix} /></div><div className="text-sm text-slate-500 mt-2">{s.label}</div></StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-14 max-w-2xl"><p className="overline mb-4">What We Stand For</p><h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">Four principles, zero exceptions</h2></Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {VALUES.map((v) => (
              <StaggerItem key={v.title}>
                <div className="flex gap-5 rounded-2xl border border-slate-800/80 bg-[#111827]/60 p-7 hover:border-amber-500/30 transition-all">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400"><v.icon className="w-6 h-6" /></span>
                  <div><h3 className="text-lg font-semibold text-white mb-2">{v.title}</h3><p className="text-sm text-slate-400 leading-relaxed">{v.desc}</p></div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 sm:py-24 border-y border-slate-800/80 bg-[#0d1220]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-14 max-w-2xl"><p className="overline mb-4">Our Journey</p><h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">A decade in the making</h2></Reveal>
          <div className="relative">
            <div className="absolute left-[7px] sm:left-1/2 top-0 bottom-0 w-px bg-slate-800 sm:-translate-x-1/2" />
            <div className="space-y-10">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.year} delay={i * 0.05} className={`relative pl-8 sm:pl-0 sm:grid sm:grid-cols-2 sm:gap-12 ${i % 2 === 0 ? "" : "sm:[direction:rtl]"}`}>
                  <div className={`[direction:ltr] ${i % 2 === 0 ? "sm:text-right sm:pr-12" : "sm:pl-12"}`}>
                    <div className="absolute left-0 sm:left-1/2 top-1.5 h-3.5 w-3.5 rounded-full bg-amber-400 ring-4 ring-[#0d1220] sm:-translate-x-1/2" />
                    <div className="font-accent text-2xl font-bold text-amber-400 mb-1">{t.year}</div>
                    <h3 className="text-lg font-semibold text-white mb-1">{t.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{t.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-14 max-w-2xl"><p className="overline mb-4">Leadership</p><h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">The people behind the placements</h2></Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {TEAM.map((m) => (
              <StaggerItem key={m.name}>
                <div className="group rounded-2xl overflow-hidden border border-slate-800/80 bg-[#111827]/60 hover-lift hover:border-amber-500/40">
                  <div className="relative h-72 overflow-hidden"><img src={m.img} alt={m.name} className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" /><div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] to-transparent" /></div>
                  <div className="p-6"><h3 className="text-lg font-semibold text-white">{m.name}</h3><div className="font-mono text-xs text-amber-400 mt-1">{m.role}</div></div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
